
import React, { useEffect, useRef } from 'react';
import { useClarity } from '../hooks/useClarity';

const UseCasePageWrapper = ({
                                children,
                                day,
                                title,
                                category,
                                type,
                                question // Optional: the main business question
                            }) => {
    const { trackEvent, setTag, trackBookingClick } = useClarity();
    const startTime = useRef(Date.now());
    const hasTrackedView = useRef(false);

    // Track page view on mount
    useEffect(() => {
        if (!hasTrackedView.current) {
            hasTrackedView.current = true;

            trackEvent('usecase_page_viewed', {
                day,
                title,
                category,
                type,
                question: question || '',
                referrer: document.referrer || 'direct'
            });

            // Set session tags for filtering in Clarity
            setTag('usecase_day', String(day));
            setTag('usecase_category', category);
            setTag('usecase_type', type);
            setTag('page_type', 'use_case_detail');
        }

        // Track time on page when leaving
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
            trackEvent('usecase_page_exit', {
                day,
                title,
                time_spent_seconds: timeSpent,
                time_spent_readable: timeSpent > 60
                    ? `${Math.floor(timeSpent / 60)}m ${timeSpent % 60}s`
                    : `${timeSpent}s`
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track scroll depth
    useEffect(() => {
        const milestones = [25, 50, 75, 100];
        const tracked = new Set();

        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollHeight <= 0) return;

            const scrolled = (window.scrollY / scrollHeight) * 100;

            milestones.forEach(milestone => {
                if (scrolled >= milestone && !tracked.has(milestone)) {
                    tracked.add(milestone);
                    trackEvent('usecase_scroll_depth', {
                        day,
                        title,
                        depth: milestone
                    });
                }
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [day, title, trackEvent]);

    // Intercept CTA button clicks
    useEffect(() => {
        const handleClick = (e) => {
            const target = e.target.closest('a, button');
            if (!target) return;

            const href = target.getAttribute('href') || '';
            // const text = target.textContent?.trim() || '';

            // Track booking/discovery call clicks
            if (href.includes('cal.com') || href.includes('discovery')) {
                trackBookingClick(`usecase-${day}-${title.toLowerCase().replace(/\s+/g, '-')}`);
            }

            // Track "View More Use Cases" clicks
            if (href.includes('/use-cases') && !href.includes('/use-cases/')) {
                trackEvent('usecase_view_more_clicked', {
                    from_day: day,
                    from_title: title
                });
            }
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [day, title, trackBookingClick, trackEvent]);

    return <>{children}</>;
};

export default UseCasePageWrapper;